import { useCallback, useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { toast } from '@/lib/toast';
import { initialTiltPermission, requestTiltPermission, TiltPermission } from './deviceGravity';

/**
 * Longer than the 4s the other toasts get, since this one asks for something rather than
 * reporting it. Timing out costs nothing: tilt stays off and the next load asks again.
 */
export const TILT_PERMISSION_TOAST_DURATION = 20000;

export const TILT_PERMISSION_TOAST = {
    message: 'Allow device permissions',
    description: 'This will show you fun animations and interactions!',
    // The tap raises iOS's own dialog rather than opening a settings screen.
    actionLabel: 'Allow',
} as const;

const TILT_PERMISSION_TRACKING = {
    dataTrackActionClick: 'Career Skill Fit Tilt Permission Clicked',
    dataTrackActionButtonName: 'skill-fit-tilt-permission',
    dataTrackCloseClick: 'Career Skill Fit Tilt Permission Toast Closed',
    dataTrackCloseButtonName: 'toast-skill-fit-tilt-permission-close',
} as const;

export interface UseTiltPermissionGateOptions {
    /** `data-track-location` for the toast's own action and close controls. */
    trackLocation: string;
}

export interface TiltPermissionGate {
    /**
     * Attach to the card, so the ask waits until there is a chart on screen to tilt. A callback
     * ref because an effect cannot see an object ref's `current` change.
     */
    sectionRef: (element: HTMLElement | null) => void;
    tiltPermission: TiltPermission;
    /** The sensor probe's way of reporting that a remembered grant is gone. */
    onTiltRevoked: () => void;
}

/**
 * Asks for motion permission through a toast of our own when the card scrolls into view. The chart
 * is up either way with its touch interaction intact, and a grant simply adds tilt to it. The
 * toast supplies the user gesture a scroll handler has none of, and keeps a refusal from spending
 * iOS's one dialog per origin.
 */
export const useTiltPermissionGate = ({
    trackLocation,
}: UseTiltPermissionGateOptions): TiltPermissionGate => {
    const prefersReducedMotion = usePrefersReducedMotion();
    const [tiltPermission, setTiltPermission] = useState<TiltPermission>(initialTiltPermission);
    // Tracked apart from the permission, which reverts to 'prompt' on a rejection so a later ask
    // stays possible. This is only "the user has answered", which is what retires the toast.
    const [isAnswered, setIsAnswered] = useState(false);
    const [sectionElement, setSectionElement] = useState<HTMLElement | null>(null);
    /** Outside the effect, so a re-run of it can never ask a second time. */
    const hasAskedRef = useRef(false);

    // Reduced motion suppresses the ask rather than answering it, so turning it back off puts the
    // question live again.
    const isAskOutstanding = !prefersReducedMotion && !isAnswered && tiltPermission === 'prompt';

    const onTiltRevoked = useCallback(() => {
        // Counts as answered, so a grant the platform has quietly dropped does not turn into a
        // fresh ask mid-session. The next load asks, the stored grant having been cleared.
        setIsAnswered(true);
        setTiltPermission('prompt');
    }, []);

    useEffect(() => {
        if (!isAskOutstanding || !sectionElement) {
            return;
        }

        const ask = () => {
            if (hasAskedRef.current) {
                return;
            }
            hasAskedRef.current = true;

            toast.info(TILT_PERMISSION_TOAST.message, {
                description: TILT_PERMISSION_TOAST.description,
                duration: TILT_PERMISSION_TOAST_DURATION,
                action: {
                    label: TILT_PERMISSION_TOAST.actionLabel,
                    onClick: () => {
                        // Nothing may be awaited ahead of this: iOS rejects a request made after
                        // the tap's activation is spent.
                        void requestTiltPermission().then((result) => {
                            // Held even when 'denied', so the next load does not offer a button
                            // iOS will ignore. A rejection comes back as 'prompt' instead.
                            setTiltPermission(result);
                            setIsAnswered(true);
                        });
                    },
                },
                // Closing the toast, or letting it time out, is an answer too.
                onDismiss: () => setIsAnswered(true),
                onAutoClose: () => setIsAnswered(true),
                dataTrackLocation: trackLocation,
                ...TILT_PERMISSION_TRACKING,
            });
        };

        if (typeof IntersectionObserver !== 'function') {
            ask();
            return;
        }

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                ask();
                observer.disconnect();
            }
        }, { threshold: 0 });
        observer.observe(sectionElement);

        return () => observer.disconnect();
    }, [isAskOutstanding, sectionElement, trackLocation]);

    return { sectionRef: setSectionElement, tiltPermission, onTiltRevoked };
};
